import {
  clearPortalSession,
  isSsoSite,
  persistSsoCompletion,
  ssoLoginPath,
  ssoReturnPath,
  ssoUserEndpoint,
  type SsoContext,
} from './ssoSession';
import { resolvePublicPortalProfile } from './publicPortalProfile';
export { parseUnifiedPortalPaths } from './unifiedPortalPaths';

type StorageReader = Pick<Storage, 'getItem'>;
type StorageWriter = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
type JsonRecord = Record<string, unknown>;

interface LocationParts {
  pathname: string;
  search: string;
  hash: string;
  /**
   * The scheme the PAGE itself is served over, used to decide whether an http broker URL may be
   * followed. Optional on purpose: when it is absent the guard stays https-only, exactly the
   * behaviour it had before this field existed, so no caller loses a check by not knowing about it.
   */
  protocol?: string;
}

export interface ActiveSessionSyncCompletionOutcome {
  kind: 'hydrate' | 'signed_out' | 'denied' | 'preserve' | 'legacy' | 'failed';
  returnPath: string;
  reason?: string;
  logoutUrl?: string;
}

export interface ActiveSessionAuthGenerationSnapshot {
  context: SsoContext;
  generation: number;
}

export interface ActiveSessionSyncReturnPath {
  activeSyncAttempted: boolean;
  returnPath: string;
}

export interface ActiveSessionSyncReloadState {
  id: number;
  context: SsoContext;
  phase: 'armed' | 'pending' | 'redirecting' | 'settled';
  curtainRevision?: number;
}

const ATTEMPT_MARKER_PREFIX = 'ra2n_sso_active_sync_attempt';
const RETURN_PATH_PREFIX = 'ra2n_sso_active_sync_return';
const AUTH_GENERATION_PREFIX = 'ra2n_sso_auth_generation';
const MIN_ACTIVE_SESSION_SYNC_COOLDOWN_MS = 1_000;
const MAX_ACTIVE_SESSION_SYNC_COOLDOWN_MS = 3_600_000;
export const ACTIVE_SESSION_AUTH_GENERATION_EVENT = 'ra2n:sso-auth-generation';
let activeSessionSyncTransitions = 0;
let reloadSyncIntent: ActiveSessionSyncReloadState | undefined;
let reloadSyncSequence = 0;
const reloadSyncListeners = new Set<() => void>();

export const readActiveSessionSyncReload = (): ActiveSessionSyncReloadState | undefined =>
  reloadSyncIntent;

export const subscribeActiveSessionSyncReload = (listener: () => void): (() => void) => {
  reloadSyncListeners.add(listener);
  return () => { reloadSyncListeners.delete(listener); };
};

const publishActiveSessionSyncReload = (state: ActiveSessionSyncReloadState | undefined): void => {
  reloadSyncIntent = state;
  reloadSyncListeners.forEach(listener => listener());
};

export const isActiveSessionSyncReloadCurrent = (state: ActiveSessionSyncReloadState): boolean =>
  reloadSyncIntent?.id === state.id;

export const settleActiveSessionSyncReload = (state: ActiveSessionSyncReloadState): boolean => {
  const current = reloadSyncIntent;
  if (!current || current.id !== state.id || current.phase === 'settled') return false;
  publishActiveSessionSyncReload({ ...current, phase: 'settled' });
  return true;
};

export const clearActiveSessionSyncReload = (state: ActiveSessionSyncReloadState): boolean => {
  if (!isActiveSessionSyncReloadCurrent(state)) return false;
  publishActiveSessionSyncReload(undefined);
  return true;
};

export const markActiveSessionSyncReloadRedirecting = (
  state: ActiveSessionSyncReloadState,
): boolean => {
  const current = reloadSyncIntent;
  if (!current || current.id !== state.id || current.phase !== 'pending') return false;
  publishActiveSessionSyncReload({ ...current, phase: 'redirecting' });
  return true;
};

export const isActiveSessionSyncTransitionInFlight = (): boolean =>
  activeSessionSyncTransitions > 0;

export const runWithActiveSessionSyncTransition = async <T>(
  operation: () => Promise<T>,
): Promise<T> => {
  activeSessionSyncTransitions += 1;
  try {
    return await operation();
  } finally {
    activeSessionSyncTransitions -= 1;
  }
};

const contextKey = (prefix: string, context: SsoContext): string =>
  `${prefix}:${context.site}:${context.entryType}`;

const readAuthGeneration = (
  context: SsoContext,
  storage: StorageReader,
): number => {
  const stored = storage.getItem(contextKey(AUTH_GENERATION_PREFIX, context));
  const parsed = stored === null ? 0 : Number(stored);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
};

export const captureActiveSessionAuthGeneration = (
  context: SsoContext,
  storage: StorageReader,
): ActiveSessionAuthGenerationSnapshot => ({
  context,
  generation: readAuthGeneration(context, storage),
});

export const isActiveSessionAuthGenerationCurrent = (
  snapshot: ActiveSessionAuthGenerationSnapshot,
  storage: StorageReader,
): boolean => readAuthGeneration(snapshot.context, storage) === snapshot.generation;

/** Whether the tab still records an auth generation for this context. */
export const hasActiveSessionAuthGeneration = (
  context: SsoContext,
  storage: StorageReader,
): boolean => storage.getItem(contextKey(AUTH_GENERATION_PREFIX, context)) !== null;

export const runForCurrentActiveSessionAuthGeneration = (
  snapshot: ActiveSessionAuthGenerationSnapshot,
  storage: StorageReader,
  operation: () => void,
): boolean => {
  if (!isActiveSessionAuthGenerationCurrent(snapshot, storage)) {
    return false;
  }

  operation();
  return true;
};

export const activeSessionSyncContextFromRequest = (
  requestUrl: string | undefined,
  currentPathname: string,
): SsoContext | undefined => {
  let requestPath = '';
  try {
    requestPath = new URL(requestUrl ?? '', 'https://p1-api.invalid').pathname;
  } catch {
    return undefined;
  }

  const exactRequest = requestPath.match(/^\/(admin|clinic|portal|massager)\/([^/]+)(?:\/|$)/);
  if (!exactRequest?.[1] || !isSsoSite(exactRequest[2])) {
    return undefined;
  }
  const site = exactRequest[2];
  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') return undefined;
  const sitePrefix = `/${site}`;
  if (!hasPathBoundaryPrefix(currentPathname, sitePrefix)) {
    return undefined;
  }
  // Business endpoints inherited by a unified portal mutate the same session
  // as `/portal/{site}` and therefore share its auth generation.
  if (exactRequest[1] === 'massager' && profile.mode !== 'unified') return undefined;
  const entryType: SsoContext['entryType'] = profile.mode === 'unified' || exactRequest[1] === 'portal'
    ? 'unified'
    : exactRequest[1] as 'admin' | 'clinic';
  if (profile.mode === 'legacy_split' && entryType === 'unified') {
    return undefined;
  }
  const segments = currentPathname.split('/');
  const matchesCurrent = entryType === 'admin'
    ? segments[2] === 'admin'
    : entryType === 'clinic'
      ? !['admin', 'massager', 'main', 'checkin'].includes(segments[2] ?? '')
      : !['admin', 'massager', 'main', 'checkin', 'login', 'sso'].includes(segments[2] ?? '')
        || currentPathname.replace(/\/+$/, '') === `${sitePrefix}/login`;

  return matchesCurrent ? { site, entryType } : undefined;
};

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasPathBoundaryPrefix = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

export const resolveActiveSessionSyncConfig = (
  enabled: unknown,
  cooldownMs: unknown,
): { cooldownMs: number } | undefined => {
  if (enabled !== 'true' || typeof cooldownMs !== 'string' || !/^\d+$/.test(cooldownMs)) {
    return undefined;
  }

  const parsed = Number(cooldownMs);
  return Number.isSafeInteger(parsed) &&
    parsed >= MIN_ACTIVE_SESSION_SYNC_COOLDOWN_MS &&
    parsed <= MAX_ACTIVE_SESSION_SYNC_COOLDOWN_MS
    ? { cooldownMs: parsed }
    : undefined;
};

export const activeSessionSyncStartEndpoint = (context: SsoContext): string => {
  switch (context.entryType) {
    case 'admin':
      return `/admin/${context.site}/sso/active-sync`;
    case 'clinic':
      return `/clinic/${context.site}/auth/sso/active-sync`;
    case 'unified':
      return `/portal/${context.site}/auth/sso/active-sync`;
  }
};

export const activeSessionSyncCompleteEndpoint = (context: SsoContext): string =>
  `${activeSessionSyncStartEndpoint(context)}/complete`;

const isPathForContext = (context: SsoContext, pathname: string): boolean => {
  const segments = pathname.split('/');
  if (segments[0] !== '' || segments[1] !== context.site) {
    return false;
  }

  switch (context.entryType) {
    case 'admin': {
      return segments[2] === 'admin' && !['login', 'sso'].includes(segments[3] ?? '');
    }
    case 'clinic':
      return !['admin', 'massager', 'login', 'sso'].includes(segments[2] ?? '');
    case 'unified': {
      return !['admin', 'massager', 'main', 'checkin', 'login', 'sso']
        .includes(segments[2] ?? '');
    }
  }
};

const safeContextPath = (
  context: SsoContext,
  candidate: string,
  fallback: string = ssoReturnPath(context),
): string => {
  if (
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes('\\') ||
    // Redirect-path validation intentionally rejects ASCII control bytes.
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u001f\u007f]/.test(candidate)
  ) {
    return fallback;
  }

  try {
    const parsed = new URL(candidate, 'https://p1.invalid');
    if (parsed.origin !== 'https://p1.invalid' || !isPathForContext(context, parsed.pathname)) {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
};

const INTENDED_PATH_PREFIX = 'ra2n_sso_intended_path';

/**
 * Remember where the person was actually going, so an SSO round trip can put them back there.
 *
 * Opening a deep link while signed out lands on the login form, and the router keeps the original
 * location in `state.from` -- but the passive SSO check is a WHOLE-PAGE redirect to the broker, and
 * router state does not survive that. Without this the completion page had nothing to go on and
 * fell back to the portal's default screen, so every cross-portal deep link silently became "the
 * home page". sessionStorage is the right home for it: same tab, cleared when the tab closes.
 *
 * Anything that is not a path inside THIS context is dropped rather than stored, so a crafted
 * `state.from` cannot turn the completion page into an open redirect.
 */
export const rememberSsoIntendedPath = (
  context: SsoContext,
  candidate: string,
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
): void => {
  const key = contextKey(INTENDED_PATH_PREFIX, context);
  const safe = safeContextPath(context, candidate, '');

  // The entry's own landing page is not an intent -- storing it would just re-assert the default.
  if (safe === '' || safe === ssoReturnPath(context)) {
    storage.removeItem(key);
    return;
  }

  storage.setItem(key, safe);
};

/** One-shot: reading it consumes it, so a later unrelated login does not reuse a stale intent. */
export const takeSsoIntendedPath = (
  context: SsoContext,
  storage: StorageWriter,
): string | undefined => {
  const key = contextKey(INTENDED_PATH_PREFIX, context);
  const stored = storage.getItem(key);
  storage.removeItem(key);

  if (stored === null) {
    return undefined;
  }

  const safe = safeContextPath(context, stored, '');
  return safe === '' ? undefined : safe;
};

export const safeActiveSessionSyncReturnPath = (
  context: SsoContext,
  location: LocationParts,
): string => safeContextPath(
  context,
  `${location.pathname}${location.search}${location.hash}`,
);

const rememberActiveSessionSyncReturnPath = (
  context: SsoContext,
  returnPath: string,
  storage: Pick<Storage, 'setItem'>,
): void => {
  storage.setItem(contextKey(RETURN_PATH_PREFIX, context), returnPath);
};

export const takeActiveSessionSyncReturnPath = (
  context: SsoContext,
  storage: StorageWriter,
): ActiveSessionSyncReturnPath => {
  const key = contextKey(RETURN_PATH_PREFIX, context);
  const stored = storage.getItem(key);
  // Completion may be reloaded before its request settles, so only finalized completion may consume this.
  return {
    activeSyncAttempted: stored !== null,
    returnPath: safeContextPath(context, stored ?? '', ssoReturnPath(context)),
  };
};

const clearActiveSessionSyncReturnPath = (
  context: SsoContext,
  storage: Pick<Storage, 'removeItem'>,
): void => {
  storage.removeItem(contextKey(RETURN_PATH_PREFIX, context));
};

const claimActiveSessionSyncAttempt = (
  context: SsoContext,
  storage: StorageWriter,
  authGenerationStorage: StorageReader,
  cooldownMs: number,
  now: number,
  userReload: boolean,
): boolean => {
  const key = contextKey(ATTEMPT_MARKER_PREFIX, context);
  const stored = storage.getItem(key);
  const generation = readAuthGeneration(context, authGenerationStorage);
  let previousAttemptAt: number | undefined;
  try {
    const parsed = stored === null ? undefined : JSON.parse(stored);
    // The timestamp is deliberately read without comparing the stored generation. Every
    // successful sync issues a new session and therefore a new generation, so gating the
    // cooldown on a generation match expired it exactly when it was needed: the reloaded
    // page saw "no previous attempt" and started another broker round trip immediately.
    if (
      isRecord(parsed) &&
      typeof parsed.at === 'number' &&
      Number.isFinite(parsed.at) &&
      parsed.at >= 0
    ) {
      previousAttemptAt = parsed.at;
    }
  } catch {
    // Ignore malformed markers once so the current auth generation can converge immediately.
  }
  // Reloading is how a user asks this site to catch up, so it always checks the broker. The
  // cooldown is there to stop the page redirecting on its own, not to overrule the person at the
  // keyboard: signing in on another Portal and pressing F5 here has to pick that session up, and
  // having to press it twice, or wait out a minute, reads as the sharing being broken. The round
  // trip the sync returns through is a `navigate`, never a `reload`, so this cannot re-enter itself.
  if (!userReload
    && previousAttemptAt !== undefined
    && now - previousAttemptAt < cooldownMs) {
    return false;
  }
  storage.setItem(key, JSON.stringify({ generation, at: now }));
  return true;
};

/**
 * Whether a boot is about to spend a broker round trip, answerable before React renders.
 *
 * The curtain that hides the pre-navigation paint goes up in the entry module, long before the
 * trigger exists, so the cheap half of the decision lives here where both callers read one rule.
 * Unlike the claim above this only peeks: raising a curtain must never consume the cooldown.
 */
export const activeSessionSyncWillLikelyRun = (userReload: boolean): boolean => userReload;

/** Arm one exact protected-shell reload before React mounts. */
export const armActiveSessionSyncReload = (context: SsoContext): void => {
  publishActiveSessionSyncReload({
    id: ++reloadSyncSequence,
    context: { ...context },
    phase: 'armed',
  });
};

/** Consume the boot intent exactly once and only for its site/entry context. */
export const claimActiveSessionSyncReload = (
  context: SsoContext,
  curtainRevision?: number,
): boolean => {
  if (
    reloadSyncIntent?.context.site !== context.site ||
    reloadSyncIntent.context.entryType !== context.entryType ||
    reloadSyncIntent.phase !== 'armed'
  ) {
    return false;
  }
  publishActiveSessionSyncReload({ ...reloadSyncIntent, phase: 'pending', curtainRevision });
  return true;
};

// Completion advances the auth generation. Rebase the per-tab attempt marker so the
// protected shell's immediate remount/focus does not mistake that same completion for
// an unrelated login/logout generation and start another broker round-trip.
export const settleActiveSessionSyncCooldown = (
  context: SsoContext,
  storage: Pick<Storage, 'setItem'>,
  authGenerationStorage: StorageReader,
  now: number = Date.now(),
): void => {
  storage.setItem(contextKey(ATTEMPT_MARKER_PREFIX, context), JSON.stringify({
    generation: readAuthGeneration(context, authGenerationStorage),
    at: now,
  }));
};

const safeActiveSessionSyncAbsoluteUrl = (
  candidate: string,
  pageProtocol?: string,
): string | undefined => {
  // Falls back to the live page scheme when the caller does not name one. Passing it explicitly
  // is still preferred (the trigger does, so its tests need no DOM), but leaving the default as
  // "https only" meant a caller that simply did not know about the parameter silently threw the
  // URL away: parseActiveSessionSyncCompletion did exactly that with the `logout_url` from a 403,
  // so the denial screen could not render its 「SSOからログアウト」 button and the operator was
  // stranded with no route back to a login form.
  const effectiveProtocol = pageProtocol
    ?? (typeof window === 'undefined' ? undefined : window.location.protocol);
  try {
    const url = new URL(candidate);
    const localHttp =
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1', '::1', '[::1]'].includes(url.hostname);
    // A page already served over http may follow an http broker URL; an https page may NOT,
    // because that is a downgrade. The loopback list above does not cover a deployment reached
    // by IP:port with no certificate -- there the broker is `http://<ip>:<port>`, every hostname
    // in the list misses, and active session sync became a SILENT no-op: the request went out,
    // the redirect came back 200, and the trigger dropped it without a log, an error or a
    // rejected promise. Measured on dev3 2026-08-28, where the symptom was P1 keeping its old
    // local session after another Portal signed in. P3 hit this same wall on the establish URL
    // and settled on this same rule (resolveSsoEstablishUrl, d5092f39).
    const samePageHttp = url.protocol === 'http:' && effectiveProtocol === 'http:';
    if (
      (url.protocol !== 'https:' && !localHttp && !samePageHttp) ||
      url.username !== '' ||
      url.password !== '' ||
      url.hash !== ''
    ) {
      return undefined;
    }
    return url.toString();
  } catch {
    return undefined;
  }
};

const activeSessionSyncRedirectUrl = (
  payload: unknown,
  pageProtocol?: string,
): string | undefined => {
  if (!isRecord(payload) || typeof payload.redirect_url !== 'string') {
    return undefined;
  }

  return safeActiveSessionSyncAbsoluteUrl(payload.redirect_url, pageProtocol);
};

export const createActiveSessionSyncTrigger = ({
  context,
  cooldownMs,
  storage,
  authGenerationStorage,
  location,
  now,
  request,
  navigate,
  userReload = false,
  isCurrent = () => true,
}: {
  context: SsoContext;
  cooldownMs: number;
  storage: StorageWriter;
  authGenerationStorage: StorageReader;
  location: () => LocationParts;
  now: () => number;
  request: (endpoint: string, payload: { return_path: string }) => Promise<unknown>;
  navigate: (redirectUrl: string) => void;
  userReload?: boolean;
  isCurrent?: () => boolean;
}): (() => Promise<void>) => {
  let inFlight: Promise<void> | undefined;

  return (): Promise<void> => {
    if (inFlight !== undefined) {
      return inFlight;
    }
    if (!claimActiveSessionSyncAttempt(
      context,
      storage,
      authGenerationStorage,
      cooldownMs,
      now(),
      userReload,
    )) {
      return Promise.resolve();
    }

    // Read once: the return path and the scheme check have to describe the SAME page, and
    // `location()` is a live getter.
    const currentLocation = location();
    const returnPath = safeActiveSessionSyncReturnPath(context, currentLocation);
    inFlight = request(activeSessionSyncStartEndpoint(context), { return_path: returnPath })
      .then(payload => {
        const redirectUrl = activeSessionSyncRedirectUrl(payload, currentLocation.protocol);
        if (redirectUrl === undefined || !isCurrent()) {
          return;
        }
        rememberActiveSessionSyncReturnPath(context, returnPath, storage);
        navigate(redirectUrl);
      })
      .catch(() => {
        // A broker miss or outage must preserve the current Portal session.
      })
      .finally(() => {
        inFlight = undefined;
      });

    return inFlight;
  };
};

export const parseActiveSessionSyncCompletion = (
  statusCode: number,
  payload: unknown,
  context: SsoContext,
  rememberedReturnPath: string,
  activeSyncAttempted: boolean = false,
): ActiveSessionSyncCompletionOutcome => {
  const fallback = safeContextPath(context, rememberedReturnPath, ssoReturnPath(context));
  const responsePayload = isRecord(payload) ? payload : undefined;
  const responseReturnPath = responsePayload && typeof responsePayload.return_path === 'string'
    ? safeContextPath(context, responsePayload.return_path, fallback)
    : fallback;
  const status = responsePayload?.status;

  if (statusCode === 200 && (status === 'switched' || status === 'unchanged')) {
    return { kind: 'hydrate', returnPath: responseReturnPath };
  }
  if (statusCode === 200 && status === 'signed_out') {
    return { kind: 'signed_out', returnPath: ssoLoginPath(context) };
  }
  if (statusCode === 403 && status === 'denied') {
    const logoutUrl = responsePayload && typeof responsePayload.logout_url === 'string'
      ? safeActiveSessionSyncAbsoluteUrl(responsePayload.logout_url)
      : undefined;
    return {
      kind: 'denied',
      reason: typeof responsePayload?.reason === 'string'
        ? responsePayload.reason
        : 'access_denied',
      returnPath: responseReturnPath,
      ...(logoutUrl === undefined ? {} : { logoutUrl }),
    };
  }
  if (statusCode === 409 || statusCode === 503) {
    return { kind: 'preserve', returnPath: responseReturnPath };
  }
  if (statusCode === 204) {
    return {
      kind: activeSyncAttempted ? 'preserve' : 'legacy',
      returnPath: fallback,
    };
  }

  return { kind: 'failed', returnPath: fallback };
};

export const normalizeActiveSessionSyncTransportError = (
  error: unknown,
): { statusCode: number; payload: unknown } => {
  if (isRecord(error) && isRecord(error.response)) {
    const statusCode = error.response.status;
    const responseData = error.response.data;
    if (typeof statusCode === 'number') {
      return {
        statusCode,
        payload: isRecord(responseData) ? responseData.data : undefined,
      };
    }
  }

  return { statusCode: 503, payload: undefined };
};

export const emitActiveSessionAuthGeneration = (
  context: SsoContext,
  storage: StorageReader & Pick<Storage, 'setItem'>,
  target: Pick<EventTarget, 'dispatchEvent'>,
): void => {
  const key = contextKey(AUTH_GENERATION_PREFIX, context);
  const next = readAuthGeneration(context, storage) + 1;
  storage.setItem(key, String(next));

  const event = typeof CustomEvent === 'undefined'
    ? new Event(ACTIVE_SESSION_AUTH_GENERATION_EVENT)
    : new CustomEvent(ACTIVE_SESSION_AUTH_GENERATION_EVENT, {
      detail: { ...context, generation: next },
    });
  target.dispatchEvent(event);
};

export const completeActiveSessionSync = async ({
  context,
  rememberedReturnPath,
  activeSyncAttempted = false,
  requestCompletion,
  loadSessionMetadata,
  localStorage,
  sessionStorage,
  eventTarget,
}: {
  context: SsoContext;
  rememberedReturnPath: string;
  activeSyncAttempted?: boolean;
  requestCompletion: (
    endpoint: string,
    payload: Record<string, never>,
  ) => Promise<{ statusCode: number; payload: unknown }>;
  loadSessionMetadata: (endpoint: string) => Promise<unknown>;
  localStorage: StorageWriter;
  sessionStorage: StorageWriter;
  eventTarget: Pick<EventTarget, 'dispatchEvent'>;
}): Promise<ActiveSessionSyncCompletionOutcome> => runWithActiveSessionSyncTransition(async () => {
  let completionGeneration = captureActiveSessionAuthGeneration(context, localStorage);
  let mayClearReturnPath = true;
  let shouldSettleCooldown = false;
  const preserveNewerSession = (): ActiveSessionSyncCompletionOutcome => {
    mayClearReturnPath = false;
    return {
      kind: 'preserve',
      returnPath: safeContextPath(context, rememberedReturnPath, ssoReturnPath(context)),
    };
  };

  try {
    const response = await requestCompletion(
      activeSessionSyncCompleteEndpoint(context),
      {},
    );
    if (!isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)) {
      return preserveNewerSession();
    }

    const outcome = parseActiveSessionSyncCompletion(
      response.statusCode,
      response.payload,
      context,
      rememberedReturnPath,
      activeSyncAttempted,
    );

    if (outcome.kind === 'hydrate') {
      try {
        const metadata = await loadSessionMetadata(ssoUserEndpoint(context));
        if (!isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)) {
          return preserveNewerSession();
        }
        persistSsoCompletion(context, metadata, localStorage, sessionStorage);
      } catch (error) {
        if (!isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)) {
          return preserveNewerSession();
        }
        clearPortalSession(context, localStorage, sessionStorage);
        emitActiveSessionAuthGeneration(context, localStorage, eventTarget);
        completionGeneration = captureActiveSessionAuthGeneration(context, localStorage);
        throw error;
      }
      emitActiveSessionAuthGeneration(context, localStorage, eventTarget);
      completionGeneration = captureActiveSessionAuthGeneration(context, localStorage);
      shouldSettleCooldown = true;
      return outcome;
    }

    if (outcome.kind === 'signed_out' || outcome.kind === 'denied') {
      if (!isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)) {
        return preserveNewerSession();
      }
      clearPortalSession(context, localStorage, sessionStorage);
      emitActiveSessionAuthGeneration(context, localStorage, eventTarget);
      completionGeneration = captureActiveSessionAuthGeneration(context, localStorage);
    }

    if (outcome.kind === 'preserve') {
      shouldSettleCooldown = true;
    }

    return outcome;
  } catch (error) {
    if (!isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)) {
      return preserveNewerSession();
    }
    throw error;
  } finally {
    if (
      mayClearReturnPath &&
      isActiveSessionAuthGenerationCurrent(completionGeneration, localStorage)
    ) {
      if (shouldSettleCooldown) {
        settleActiveSessionSyncCooldown(context, sessionStorage, localStorage);
      }
      clearActiveSessionSyncReturnPath(context, sessionStorage);
    }
  }
});
